<template>
    <modal @close="$emit('close')" maxWidth="480px" width="98%">
        <div slot="title">{{ $t('coupon_details') }}</div>
        <div slot="footer" v-if="coupon.offer.applicable_at_counter && coupon.status === 'active'" class="py-2 px-3 text-right"> 
            <button class="btn btn-sm btn-primary" @click="redeem">{{ $t('redeem')}}</button>
        </div>
        <div class="px-3 pb-3">
            <div
                v-if="coupon.offer.image?.file_name"
                class="img-container mt-3"
                :style="{background: 'url(' + context.storageEndpoint + coupon.offer.image.file_name + ') center/cover no-repeat' }">
                <img :src="context.storageEndpoint + coupon.offer.image?.file_name" style="visibility: hidden;" />
            </div>
            <div class="mt-3">
                <div class="field-label">{{$t('title')}}</div>
                <div class="field-value">
                    <div>
                        <span v-if="coupon.offer.applicable_at_counter" class="label label-tight label-muted d-inline-block mx-0">
                            {{ $t('applicable_at_counter') }}
                        </span>
                    </div>
                    {{ coupon.offer.title }}
                </div>
            </div>
            <div class="mt-3">
                <div class="field-label">{{$t('description')}}</div>
                <div class="field-value white-space-pre-line">{{ coupon.offer.description || '' }}</div>
            </div>
            <div class="mt-3">
                <div class="field-label">{{$t('expiration_date')}}</div>
                <div class="field-value">
                    <div v-if="coupon.offer.start && coupon.expired_at">
                        <span>{{ formatDate(coupon.offer.start) }}</span>
                        <span>{{ $t('general.to') }}</span>
                        <span>{{ formatDate(coupon.expired_at) }}</span>
                    </div>
                    <div v-else>{{ $t('no_expiration_date') }}</div>
                </div>
            </div>
            <div class="mt-3">
                <div class="field-label">{{$t('discount_amount')}}</div>
                <div class="field-value blue-highlight">
                    <div>{{ formatValue(coupon.offer) }}</div>
                    <div v-if="getMinOrderAmountText(coupon.offer)" class="text-muted p-0">
                        <small>{{ getMinOrderAmountText(coupon.offer) }}</small>
                    </div>
                </div>
            </div>
            <div class="mt-3">
                <div class="field-label">{{$t('status')}}</div>
                <div class="field-value">
                    <div class="label text-nowrap" :class="classForCoupon(coupon)" 
                        v-b-tooltip.hover.bottom :title="$t('booking_display_id', {displayId: coupon.booking_display_id})" >
                        {{ $t('coupon_status_' + coupon.status) }}</div>
                    <div v-if="coupon.last_redeemed_at" class="text-muted">
                        <small>{{ $t('redeemed_at', {redeemedDate: formatDate(coupon.last_redeemed_at)}) }}</small>
                    </div>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import moment from 'moment';
import 'moment-timezone';
import Modal from '../common/Modal.vue';
import { mapGetters } from 'vuex';
import { localizedAmount } from '../../currency';

export default {
    props: {
        provider: { type: Object, required: true },
        context: { type: Object, required: true },
        coupon: {type: Object, required: true},
    },
    components: { Modal },
    mixins: [api, promptConfirm],
    data() {
        return {
            redeemPassword: ''
        }
    },
    computed: {
        ...mapGetters({
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
    },
    methods: {
        formatDate(date) {
            return moment.tz(date, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD');
        },
        formatValue(offer) {
            if (this.customerId) {
                if (offer.rule_type == 'FIXED_AMOUNT') {
                    return '-$' + offer.value;
                } else if (offer.rule_type == 'PERCENTAGE') {
                    return '-' + offer.value + '%';
                }
            } else {
                if (offer.rule_type == 'FIXED_AMOUNT') {
                    return this.$t('fixed_amount_text', { value: localizedAmount(offer.value, this.provider.currency) });
                } else if (offer.rule_type == 'PERCENTAGE') {
                    const isZh = getSharedLang() === 'zh';
                    let value = offer.value;
                    if (isZh) {
                        value = 100 - offer.value;
                    }
                    if (value % 10 == 0) {
                        value /= 10;
                    }
                    return this.$t('percentage_text', {value});
                }
            }
        },
        getMinOrderAmountText(offer) {
            const minOrderAmountCondition = offer.conditions.find(item => item.type == 'MIN_ORDER_AMOUNT');
            if (minOrderAmountCondition) {
                const extras = JSON.parse(minOrderAmountCondition.extras);
                return this.$t('min_order_amount_text', { value: this.getExchangedAmount(extras.min) })
            } else {
                return null;
            }
        },
        classForCoupon(coupon) {
            const labelVariant = {
                active: 'label-success',
                expired: 'label-danger',
                redeemed: 'label-muted',
                disabled: 'label-danger'
            }[coupon.status.toLowerCase()]
            return {[labelVariant]: true}
        },
        redeem() {
            this.promptInput(
                this.$t('redeem_coupon'),
                null,
                this.$t('please_enter_redeem_password'),
                this.$t('redeem'),
                (password) => {
                    const close = this.promptLoading();
                    this.redeemCustomerOffer(this.coupon.id, password, (response, error) => {
                        close();
                        if (error) {
                            const message = error.response?.data?.error ?? error;
                            this.promptAlert(message);
                        } else {
                            this.promptAlert(
                                this.$t('success_redeemed'),
                                () => { 
                                    this.$emit('update-coupon', response.data);
                                },
                                'success'
                            )
                        }
                    })
                }
            )
        },
        getExchangedAmount(amount) {
            return getExchangedAmount({
                rateMap: this.currencyRateMap,
                amount,
                from: this.provider.currency || 'TWD',
                to: this.currency,
                currencyOptions: this.currencyOptions,
            });
        },
    }
}
</script>

<style scoped lang="scss">
    .field-label {
        color: #90A4AE;
        word-break: break-all;
        width: 100%;
        font-size: 0.8em;
        margin-bottom: 0.5em;
    }

    .field-value {
        word-break: break-all;
        font-size: 1.1em;
        color: #31393C;
        margin-bottom: 14px;
    }

    .blue-highlight {
        color: #0094FB;
    }

    .img-container {
        width: 100%;

        img {
            width: 100%;
        }
    }

</style>

<i18n>
{
    "en": {
        "coupon_details": "Details",
        "no_expiration_date": "Permanent",
        "title": "Title",
        "expiration_date": "Expired Date",
        "discount_amount": "Discount",
        "description": "Description",
        "status": "Status",
        "coupon_status_active": "Active",
        "coupon_status_expired": "Expired",
        "coupon_status_redeemed": "Redeemed",
        "coupon_status_disabled": "Disabled",
        "redeemed_at": "Redeemed at {redeemedDate}",
        "fixed_amount_text": "{value} off",
        "percentage": "{value}% off",
        "min_order_amount_text": "Applicable when over {value}",
        "coupon_type_percent_off_zh": "{offPercentage} Off",
        "booking_display_id": "Booking ID {displayId}",
        "redeem": "Redeem",
        "redeem_coupon": "Coupon Redeem",
        "please_enter_redeem_password": "Please enter password for redeem",
        "success_redeemed": "Coupon redeem successfully",
        "applicable_at_counter": "Redeemable At Counter"
    },
    "zh": {
        "coupon_details": "詳細資訊",
        "no_expiration_date": "永久有效",
        "title": "標題",
        "expiration_date": "有效期限",
        "discount_amount": "折扣",
        "description": "使用說明",
        "status": "狀態",
        "coupon_status_active": "有效",
        "coupon_status_expired": "已過期",
        "coupon_status_redeemed": "已使用",
        "coupon_status_disabled": "停用",
        "redeemed_at": "於 {redeemedDate} 使用",
        "fixed_amount_text": "折 {value}",
        "percentage_text": "{value} 折",
        "min_order_amount_text": "滿 {value} 即可使用",
        "coupon_type_percent_off_en": "{offPercentage} OFF",
        "booking_display_id": "預約編號 {displayId}",
        "redeem": "進行核銷",
        "redeem_coupon": "優惠券核銷",
        "please_enter_redeem_password": "請輸入核銷碼",
        "success_redeemed": "優惠券核銷成功",
        "applicable_at_counter": "現場核銷"
    }
}
</i18n>